import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Repeat, Copy, Check } from 'lucide-react';

// Local date string to avoid UTC timezone offset bugs
const getLocalDateStr = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TaskDialog = ({ open, onClose, onSave, task, mode = 'add', preSelectedCategory = 'A', selectedDate, categoryTaskCount = 0 }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: preSelectedCategory,
    dueDate: getLocalDateStr(selectedDate || new Date()),
    status: '',
    completed: false,
    isRecurring: false,
    recurrenceFrequency: 'daily',
    manualPosition: null
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        category: task.category || 'A',
        dueDate: task.due_date || task.dueDate || '',
        status: task.status || '',
        completed: task.completed || false,
        isRecurring: task.is_recurring || task.isRecurring || false,
        recurrenceFrequency: task.recurrence_frequency || task.recurrenceFrequency || 'daily',
        manualPosition: task.manual_position || task.manualPosition || null
      });
    } else {
      const defaultDate = getLocalDateStr(selectedDate || new Date());
      setFormData({
        title: '',
        description: '',
        category: preSelectedCategory || 'A',
        dueDate: defaultDate,
        status: '',
        completed: false,
        isRecurring: false,
        recurrenceFrequency: 'daily',
        manualPosition: null
      });
    }
    setCopied(false);
  }, [task, mode, open, preSelectedCategory, selectedDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    onSave(formData);
    onClose();
  };

  const handleCopyTask = async () => {
    const textToCopy = formData.description 
      ? `${formData.title}\n\n${formData.description}`
      : formData.title;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl">
            {mode === 'edit' ? 'Edit Task' : 'Add New Task'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'edit' ? 'Edit the details of your task' : 'Fill in the details to create a new task'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="title" className="text-sm">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title"
              className="h-9"
              required
              data-testid="task-title-input"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="description" className="text-sm">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add task description"
              className="min-h-[60px] resize-none text-sm"
              data-testid="task-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="category" className="text-sm">Priority *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="h-9" data-testid="task-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A - High</SelectItem>
                  <SelectItem value="B">B - Medium</SelectItem>
                  <SelectItem value="C">C - Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="dueDate" className="text-sm">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="h-9 dark:[color-scheme:dark]"
                data-testid="task-duedate-input"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="status" className="text-sm">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="h-9" data-testid="task-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="in-process">In-Process</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority Position - Show for both add and edit */}
          <div className="space-y-1">
            <Label htmlFor="manualPosition" className="text-sm">Priority Position (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="manualPosition"
                type="number"
                min="1"
                placeholder="Auto"
                value={formData.manualPosition || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  manualPosition: e.target.value ? parseInt(e.target.value) : null 
                })}
                className="h-9 w-24"
                data-testid="task-priority-input"
              />
              <span className="text-xs text-slate-500">Leave empty for auto</span>
            </div>
          </div>

          {/* Recurring Task Section - More compact */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="recurring"
                checked={formData.isRecurring}
                onCheckedChange={(checked) => setFormData({ ...formData, isRecurring: checked })}
                data-testid="task-recurring-checkbox"
              />
              <Label htmlFor="recurring" className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Repeat className="w-3.5 h-3.5 text-blue-600" />
                <span>Recurring task</span>
              </Label>
            </div>

            {formData.isRecurring && (
              <div className="space-y-1 pl-6">
                <Select
                  value={formData.recurrenceFrequency}
                  onValueChange={(value) => setFormData({ ...formData, recurrenceFrequency: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Copy Button - Only show in edit mode */}
          {mode === 'edit' && (
            <div className="pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyTask}
                className="w-full h-8 text-sm"
                data-testid="copy-task-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                    Copy Task
                  </>
                )}
              </Button>
            </div>
          )}

          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8">
              Cancel
            </Button>
            <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="task-save-btn">
              {mode === 'edit' ? 'Update' : 'Add Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialog;
